import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { bytesToMb } from 'utils'

import { MIME_TYPES } from '../../../const/file-mime-type'

const USER_FRIENDLY_EXTENSIONS = {
  [MIME_TYPES['.gif']]: ['.gif'],
  [MIME_TYPES['.jpeg']]: ['.jpg', '.jpeg'],
  [MIME_TYPES['.png']]: ['.png'],
  [MIME_TYPES['.doc']]: ['.doc'],
  [MIME_TYPES['.docx']]: ['.docx'],
  [MIME_TYPES['.pdf']]: ['.pdf'],
}

export type UserFriendlyExtension = keyof typeof USER_FRIENDLY_EXTENSIONS

const getUserFriendlyExtensions = (mimeType: UserFriendlyExtension[]): string[] => {
  return mimeType.reduce((acc, curr) => {
    const extensions = USER_FRIENDLY_EXTENSIONS[curr]
    if (extensions) {
      return [...acc, ...extensions]
    }
    return acc
  }, [] as string[])
}

export interface UseLoadFileOptions {
  /** megabyte*/
  maxFileSize?: number
  name: string
  /** alternative accept attribute of input file */
  accept?: UserFriendlyExtension[]

  /** clear prev value after reopen select window */
  clearPrevValue?: boolean
}

interface UseLoadFileInfo {
  url: null | string
  name: null | string
}

export const useLoadFile = ({ maxFileSize, name, accept, clearPrevValue }: UseLoadFileOptions) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { current: acceptUserFriendly } = useRef(accept?.join(', '))

  const {
    setError,
    clearErrors,
    setValue,
    register,
    formState: { errors },
  } = useFormContext()

  const [fileInfo, setFileInfo] = useState<UseLoadFileInfo | null>(null)

  const validateFile = useCallback(
    (file: File) => {
      if (maxFileSize && bytesToMb(file.size) > maxFileSize) {
        setError(
          name,
          { message: `File cannot be larger than ${maxFileSize} MB` },
          { shouldFocus: false },
        )
        return false
      }

      if (accept && !accept.includes(<UserFriendlyExtension>file.type)) {
        setError(
          name,
          {
            message: `Please select supported file type: ${getUserFriendlyExtensions(accept).join(
              ', ',
            )}`,
          },
          { shouldFocus: false },
        )
        return false
      }

      return true
    },
    [name, maxFileSize, setError, accept],
  )

  const clear = useCallback(() => {
    setFileInfo(null)
    setValue(name, null)
  }, [name, setValue])

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]

      if (!file) return clearPrevValue && clear()
      if (!validateFile(file)) return clear()

      clearErrors(name)
      setValue(name, file)
      setFileInfo({
        url: URL.createObjectURL(file).toString(),
        name: file.name,
      })
    },
    [clearPrevValue, clear, validateFile, clearErrors, name, setValue],
  )

  useEffect(() => {
    register(name)
  }, [name, register])

  return {
    fieldError: errors[name],
    name,
    inputRef,
    accept: acceptUserFriendly,
    onChange,
    clear,
    fileInfo,
  }
}
